/**
 * Link and note attachments on a chat message, as the search bar sends them
 * and the chat routes read them.
 *
 * They ride the AI SDK's `experimental_attachments` like a photo does, as
 * small data URLs:
 *   link: { name: <normalized url>, contentType: "text/uri-list", url: "data:text/uri-list;base64,..." }
 *   note: { name: "note",           contentType: "text/plain",    url: "data:text/plain;base64,..." }
 *
 * The routes fold them into framed text before the model sees them
 * (frontend/lib/ai-search/fold-attachments.ts); left alone, the SDK would
 * hand the raw URL or note to the model as an unlabelled text part.
 *
 * Client-safe (btoa / atob / TextEncoder exist in browsers and in Node).
 */

export const LINK_ATTACHMENT_TYPE = "text/uri-list";
export const NOTE_ATTACHMENT_TYPE = "text/plain";
export const NOTE_ATTACHMENT_NAME = "note";

export interface ChatAttachmentLike {
  name?: string;
  contentType?: string;
  url: string;
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

/** A UTF-8 text as a base64 data URL. */
export function encodeTextDataUrl(mime: string, text: string): string {
  return `data:${mime};base64,${bytesToBase64(new TextEncoder().encode(text))}`;
}

/** The text of a data URL (base64 or percent-encoded), or null when it is not one. */
export function decodeTextDataUrl(url: string | null | undefined): string | null {
  if (typeof url !== "string") return null;
  const match = /^data:([^,]*?),([\s\S]*)$/.exec(url);
  if (!match) return null;
  const isBase64 = /;base64$/i.test(match[1]);
  try {
    if (isBase64) {
      const binary = atob(match[2].replace(/\s+/g, ""));
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      return new TextDecoder("utf-8", { fatal: false }).decode(bytes);
    }
    return decodeURIComponent(match[2]);
  } catch {
    return null;
  }
}

export function linkToChatAttachment(url: string): ChatAttachmentLike {
  return { name: url, contentType: LINK_ATTACHMENT_TYPE, url: encodeTextDataUrl(LINK_ATTACHMENT_TYPE, `${url}\r\n`) };
}

export function noteToChatAttachment(text: string): ChatAttachmentLike {
  return { name: NOTE_ATTACHMENT_NAME, contentType: NOTE_ATTACHMENT_TYPE, url: encodeTextDataUrl(NOTE_ATTACHMENT_TYPE, text) };
}

export function isLinkAttachment(att: Pick<ChatAttachmentLike, "contentType">): boolean {
  return (att.contentType ?? "").toLowerCase().startsWith(LINK_ATTACHMENT_TYPE);
}

export function isNoteAttachment(att: Pick<ChatAttachmentLike, "contentType">): boolean {
  return (att.contentType ?? "").toLowerCase().startsWith(NOTE_ATTACHMENT_TYPE);
}

export function isImageAttachment(att: Pick<ChatAttachmentLike, "contentType">): boolean {
  return (att.contentType ?? "").toLowerCase().startsWith("image/");
}

/** The URLs of a text/uri-list body: one per line, `#` lines are comments. */
export function uriListUrls(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line && !line.startsWith("#"));
}

/** The links and notes riding a message (images left out), decoded. */
export function readTextAttachments(
  attachments: readonly ChatAttachmentLike[] | null | undefined,
): { links: string[]; notes: string[]; unreadable: number } {
  const links: string[] = [];
  const notes: string[] = [];
  let unreadable = 0;
  for (const att of attachments ?? []) {
    if (isLinkAttachment(att)) {
      const body = decodeTextDataUrl(att.url);
      const urls = body ? uriListUrls(body) : [];
      if (urls.length === 0 && typeof att.name === "string" && /^https?:\/\//i.test(att.name)) urls.push(att.name);
      if (urls.length === 0) unreadable++;
      links.push(...urls);
    } else if (isNoteAttachment(att)) {
      const body = decodeTextDataUrl(att.url);
      if (body === null) unreadable++;
      else if (body.trim()) notes.push(body);
    }
  }
  return { links, notes, unreadable };
}
